import classnames from 'classnames'
import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link } from 'react-router-dom'

import { Avatar, IMAGE_SIZES } from 'images'
import { actions, selectors } from 'tournaments'
import { ProgressBar } from 'ui'

import type { FC } from 'react'

import type { RelevantTeams, TournamentStagesTeamsStats } from 'tournaments/src/schemas/TournamentSchema'

const LessBeaten: FC<{ tournamentId: string }> = ({ tournamentId }) => {
  const dispatch = useDispatch()
  const store = useSelector(selectors.tournament)

  useEffect(() => {
    if (tournamentId && store.key !== tournamentId) {
      dispatch(actions.tournamentFetch({ tournamentId }))
    }

    return () => {
      dispatch(actions.tournamentFetchCancel())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const relevantTeams = store.data?.[0]?.relevant_teams || []
  const teamStats = (store.data?.[0]?.tournament.teams_stats || [])
    .map((stats) => ({
      ...stats,
      against: stats.matches_total > 0 ? stats.goals_against / stats.matches_total : 0,
      team: relevantTeams.find(({ id }) => id === stats.team.id),
    }))
    .filter(
      (
        stat,
      ): stat is { against: number } & {
        team: RelevantTeams
      } & TournamentStagesTeamsStats => !!stat.team && stat.matches_total > 0,
    )
    .sort((a, b) => b.matches_total - a.matches_total || a.goals_against - b.goals_against)

  return (
    <div className="LessBeaten p-2 bg-white">
      {store.loading && <ProgressBar value={100} />}
      <div className="border rounded overflow-x-auto">
        <table className="text-left border-collapse bg-white whitespace-nowrap">
          <thead>
            <tr className="*:border-r *:px-2 *:py-1">
              <th className="text-right" scope="col">
                #
              </th>
              <th scope="col">Equipo</th>
              <th className="!px-4" scope="col" title="Goles en contra">
                GC
              </th>
              <th className="!px-4" scope="col" title="Partidos jugados">
                PJ
              </th>
              <th className="!px-2" scope="col" title="Promedio de goels en contra">
                <small>GC/PJ</small>
              </th>
            </tr>
          </thead>
          <tbody>
            {teamStats.map(({ team, ...stat }, index) => (
              <tr
                key={stat.id}
                className={classnames(
                  '*:border-r *:border-t *:px-2 *:py-1 hover:bg-blueLightest',
                  index > 1 && (index + 1) % 4 === 0 && 'bg-greyLightest',
                )}
              >
                <td className="text-right">
                  <b>{index + 1}</b>
                </td>
                <td className="!p-0">
                  <div className="px-2 py-1 border border-transparent hover:border-green focus-within:border-green hover:shadow focus-within:shadow rounded">
                    <div className="flex gap-1 items-center px-1 pt-1">
                      <Link className="shrink-0 font-bold truncate !no-underline" tabIndex={-1} to={`/sst/${team.id}`}>
                        <Avatar
                          fallbackIconName="shield"
                          size="2.5em"
                          src={team.avatar}
                          srcImageSize={IMAGE_SIZES.FIT_100_100}
                        />
                      </Link>
                      <div className="grow flex flex-col overflow-hidden">
                        <Link className="font-bold truncate" to={`/sst/${team.id}`}>
                          {team.name}
                        </Link>
                        <div className="text-sm truncate text-greyDark">{team.nickname}</div>
                      </div>
                    </div>
                  </div>
                </td>
                <td className="text-right">{stat.goals_against}</td>
                <td className="text-right">{stat.matches_total}</td>
                <td className="text-right">{stat.against.toFixed(1)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default LessBeaten
